import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useStepUpVerification } from "../../application/hooks/useStepUpVerification";
import { useAuthenticatorSetup } from "../../application/hooks/useAuthenticatorSetup";
import {
  OTP_PREFERENCE_EMAIL,
  OTP_PREFERENCE_TOTP,
  type OtpPreference,
} from "../../domain/constants/otpConstants";
import type { QrCode } from "../../domain/entities/QrCode";
import { AuthenticatorSetupStep } from "./AuthenticatorSetupStep";
import { isSixDigitCode } from "../../domain/utils/otpCode";
import { OtpCodeInput } from "./OtpCodeInput";

const RESEND_COOLDOWN_SECONDS = 30;

type SubStep = "method" | "authenticatorSetup" | "code";

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

interface VerifyIdentityStepProps {
  /** Token purpose sent to send-email-otp / verify-otp / verify-totp. */
  purpose: string;
  email: string | null;
  totpEnabled: boolean;
  defaultMethod: OtpPreference;
  /** Verify only with this method; the method choice isn't shown. */
  onlyMethod?: OtpPreference;
  /** Shown above the method choice, e.g. after the previous token expired. */
  notice?: string | null;
  idPrefix: string;
  onAuthenticatorEnabled?: () => void;
  /** Receives the purpose's temp token; the step shows a spinner while it resolves. */
  onVerified: (tempAccessToken: string) => void | Promise<void>;
  /** Back from the method choice, i.e. leave this step. */
  onBack: () => void;
}

/**
 * Dialog step that re-verifies a signed-in user by email code or authenticator
 * app, setting the authenticator up inline when it isn't configured yet.
 */
export function VerifyIdentityStep({
  purpose,
  email,
  totpEnabled,
  defaultMethod,
  onlyMethod,
  notice,
  idPrefix,
  onAuthenticatorEnabled,
  onVerified,
  onBack,
}: VerifyIdentityStepProps) {
  const { t } = useTranslation("settings");
  const {
    sendEmailCode,
    verifyEmailCode,
    verifyAuthenticatorCode,
    loading: verifying,
  } = useStepUpVerification(purpose);
  const { startAuthenticatorSetup, loading: loadingSetup } = useAuthenticatorSetup();

  const [subStep, setSubStep] = useState<SubStep>("method");
  const [method, setMethod] = useState<OtpPreference>(onlyMethod ?? defaultMethod);
  const [setUpAuthenticatorHere, setSetUpAuthenticatorHere] = useState(false);
  const [qrCode, setQrCode] = useState<QrCode | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeNotice, setCodeNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [finishing, setFinishing] = useState(false);

  const loading = verifying || loadingSetup || finishing;
  const authenticatorReady = totpEnabled || setUpAuthenticatorHere;

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const timer = window.setInterval(() => {
      setResendSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendSecondsLeft]);

  const goTo = (next: SubStep) => {
    setCode("");
    setCodeError(null);
    setFormError(null);
    setSubStep(next);
  };

  const sendCode = async () => {
    if (!email) {
      setFormError(t("twoFactor.emailMissingError"));
      return false;
    }
    try {
      await sendEmailCode(email);
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      return true;
    } catch (err) {
      setFormError(errorMessage(err, t("verifyIdentity.sendCodeError")));
      return false;
    }
  };

  const handleMethodContinue = async () => {
    setFormError(null);

    if (method === OTP_PREFERENCE_EMAIL) {
      if (await sendCode()) {
        setCodeNotice(null);
        goTo("code");
      }
      return;
    }

    if (authenticatorReady) {
      setCodeNotice(null);
      goTo("code");
      return;
    }

    try {
      // A fresh secret is issued per call, so only fetch once per visit.
      if (!qrCode) setQrCode(await startAuthenticatorSetup());
      goTo("authenticatorSetup");
    } catch (err) {
      setFormError(errorMessage(err, t("verifyIdentity.setupLoadError")));
    }
  };

  const handleSetupConfirmed = () => {
    setSetUpAuthenticatorHere(true);
    onAuthenticatorEnabled?.();
    setCodeNotice(t("verifyIdentity.setupDoneNotice"));
    goTo("code");
  };

  const handleVerifyCode = async () => {
    if (!isSixDigitCode(code)) {
      setCodeError(t("verifyIdentity.codeInvalidFormat"));
      return;
    }
    if (!email) {
      setFormError(t("twoFactor.emailMissingError"));
      return;
    }

    let token: string;
    try {
      token =
        method === OTP_PREFERENCE_TOTP
          ? await verifyAuthenticatorCode(email, code)
          : await verifyEmailCode(email, code);
    } catch (err) {
      setCodeError(errorMessage(err, t("verifyIdentity.verifyError")));
      return;
    }

    setFinishing(true);
    try {
      await onVerified(token);
    } finally {
      setFinishing(false);
    }
  };

  const handleResend = async () => {
    if (loading || resendSecondsLeft > 0) return;
    setFormError(null);
    if (await sendCode()) {
      setCodeNotice(t("verifyIdentity.codeResent"));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (subStep === "method") void handleMethodContinue();
    else void handleVerifyCode();
  };

  if (subStep === "authenticatorSetup" && qrCode) {
    return (
      <AuthenticatorSetupStep
        idPrefix={idPrefix}
        qrCode={qrCode}
        onConfirmed={handleSetupConfirmed}
        onBack={() => goTo("method")}
      />
    );
  }

  const methodHint = (option: OtpPreference) => {
    if (option === OTP_PREFERENCE_EMAIL) {
      return t("verifyIdentity.methodEmailHint", { email: email ?? "" });
    }
    return authenticatorReady
      ? t("verifyIdentity.methodAuthenticatorHint")
      : t("verifyIdentity.methodAuthenticatorSetupHint");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {subStep === "method" && (
        <>
          {notice && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{notice}</p>
          )}
          {onlyMethod ? (
            <p className="text-sm text-gray-500">{methodHint(onlyMethod)}</p>
          ) : (
            <>
              <p className="text-sm text-gray-500">{t("verifyIdentity.methodHint")}</p>
              <RadioGroup
                value={method}
                onValueChange={(value) => setMethod(value as OtpPreference)}
                className="gap-3"
              >
                {[OTP_PREFERENCE_TOTP, OTP_PREFERENCE_EMAIL].map((option) => (
                  <Label
                    key={option}
                    htmlFor={`${idPrefix}-method-${option}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border p-4 font-normal transition-colors",
                      method === option
                        ? "border-primary bg-[#EAF2FF]"
                        : "border-gray-200 hover:bg-gray-50",
                    )}
                  >
                    <RadioGroupItem
                      id={`${idPrefix}-method-${option}`}
                      value={option}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {option === OTP_PREFERENCE_TOTP
                          ? t("verifyIdentity.methodAuthenticator")
                          : t("verifyIdentity.methodEmail")}
                      </span>
                      <span className="mt-1 block text-sm text-gray-500">
                        {methodHint(option)}
                      </span>
                    </span>
                  </Label>
                ))}
              </RadioGroup>
            </>
          )}
        </>
      )}

      {subStep === "code" && (
        <>
          <p className="text-sm text-gray-500">
            {method === OTP_PREFERENCE_TOTP
              ? t("verifyIdentity.codeHintAuthenticator")
              : t("verifyIdentity.codeHintEmail", { email: email ?? "" })}
          </p>
          {codeNotice && (
            <p className="rounded-lg bg-[#EAF2FF] px-3 py-2 text-sm text-primary">{codeNotice}</p>
          )}
          <OtpCodeInput
            id={`${idPrefix}-code`}
            value={code}
            onChange={(value) => {
              setCode(value);
              setCodeError(null);
            }}
            error={codeError}
            disabled={loading}
          />
          {method === OTP_PREFERENCE_EMAIL && (
            <p className="text-sm text-gray-600">
              {t("verifyIdentity.noCode")}{" "}
              <button
                type="button"
                onClick={() => void handleResend()}
                disabled={loading || resendSecondsLeft > 0}
                className={cn(
                  "font-medium",
                  loading || resendSecondsLeft > 0
                    ? "cursor-not-allowed text-gray-400"
                    : "cursor-pointer text-primary",
                )}
              >
                {resendSecondsLeft > 0
                  ? t("verifyIdentity.resendIn", { seconds: resendSecondsLeft })
                  : t("verifyIdentity.resend")}
              </button>
            </p>
          )}
        </>
      )}

      {formError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</p>
      )}

      <DialogFooter className="gap-2 sm:gap-2">
        <Button className="cursor-pointer"
          type="button"
          variant="outline"
          onClick={() => (subStep === "method" ? onBack() : goTo("method"))}
          disabled={loading}
        >
          {t("verifyIdentity.back")}
        </Button>
        <Button className="cursor-pointer" type="submit" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {subStep === "method" ? t("verifyIdentity.continue") : t("verifyIdentity.verify")}
        </Button>
      </DialogFooter>
    </form>
  );
}
